import { Box, Divider, Drawer, IconButton, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { CustomerPortalCustomer, CustomerPortalInvoice } from '@vy/types';
import { isNullOrUndefined } from '@vy/utils';
import React, { useCallback } from 'react';
import { useGetCustomerPortalInvoicePdf } from '../../../../../hooks/requests/use-customer-portal';
import { saveInvoiceAsFile } from '../../../../../services/invoices-file-saver';
import { CustomerPortalChartSection } from './CustomerPortalChartSection';
import { CustomerPortalPdfSection } from './CustomerPortalPdfSection';

type CustomerPortalInvoiceViewerProps = {
  selectedInvoice: CustomerPortalInvoice;
  customer: CustomerPortalCustomer;
  toggleDrawer: () => void;
  drawerOpen: boolean;
  token: string;
};

export const CustomerPortalInvoiceViewer: React.FC<CustomerPortalInvoiceViewerProps> = ({
  selectedInvoice,
  drawerOpen,
  toggleDrawer,
  customer,
  token,
}) => {
  const { data, isLoading } = useGetCustomerPortalInvoicePdf(token, selectedInvoice.id);

  const handleDownloadPdf = useCallback(async () => {
    if (isNullOrUndefined(data)) return;
    await saveInvoiceAsFile(data.encodedPdf, selectedInvoice.customerName, selectedInvoice.name);
  }, [data, selectedInvoice.customerName, selectedInvoice.issuedAt]);

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => toggleDrawer()}
      sx={{
        width: '767px',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: '767px',
          height: '100vh',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ECEDEE',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.default',
          px: 3,
          py: 2,
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{
          color: "text.primary"
        }}>
          {customer.name} - {selectedInvoice.name}
        </Typography>

        <IconButton onClick={handleDownloadPdf} disabled={isLoading}>
          <VyIcon icon="Download" />
        </IconButton>
      </Box>
      <Divider />
      <CustomerPortalChartSection
        token={token}
        invoiceId={selectedInvoice.id}
        productsConsumption={selectedInvoice.productsConsumption}
      />
      <CustomerPortalPdfSection isLoading={isLoading} encodedPdf={data?.encodedPdf} />
    </Drawer>
  );
};
